import type { CloseRow } from "../disconnect.ts";
import type { ModerationDesk } from "./desk.ts";
import type { ModerationNotices } from "./notices.ts";

/**
 * What the scene tells the page's moderation and asks of it (ADR-056's amendment of 2026-10-04, items 9 and 10):
 * the three server messages as its connection reads them, the connection's end, whether the player is frozen, and
 * the notice a close with 4008 shows.
 */

/**
 * A moderation message the connection read: the gateway's offer of the actions an account's roles allow, what a
 * player a GM acted on is told, or an alert to the staff. The words are the server's, which a later server may
 * extend; each reader keeps the ones it knows.
 */
export type ModerationEvent =
  | { readonly kind: "offer"; readonly actions: readonly string[] }
  | { readonly kind: "notice"; readonly what: string; readonly reason: string; readonly endsInMs: number }
  | { readonly kind: "alert"; readonly familyName: string; readonly by: string; readonly text: string };

/**
 * The page's moderation as the scene sees it.
 */
export interface ModerationFeed {
  /**
   * Hands over a moderation message the connection read.
   *
   * @param event The message.
   */
  readonly receive: (event: ModerationEvent) => void;
  /**
   * Whether a GM froze the player, read each frame: while true the scene sends no movement.
   *
   * @returns True while a freeze stands.
   */
  readonly frozen: () => boolean;
  /**
   * Tells whether the page's own character reads held in the snapshot just applied (`Away.Held`), which decides
   * whether the freeze's notice and the release's line promise safety (`ModerationNotices.setHeld`).
   *
   * @param held Whether the own character reads held.
   */
  readonly held: (held: boolean) => void;
  /**
   * The notice for a close with 4008, from what the gateway told just before it.
   *
   * @returns The notice's row, or null when nothing was told.
   */
  readonly closed: () => CloseRow | null;
  /**
   * The connection ended: what it held is forgotten, and the offer is taken back until the next join.
   */
  readonly ended: () => void;
}

/**
 * Builds the feed over the player's notices and the GM's desk.
 *
 * @param notices The notices.
 * @param desk The desk.
 * @returns The feed.
 */
export function moderationFeed(notices: ModerationNotices, desk: ModerationDesk): ModerationFeed {
  return {
    receive: (event) => {
      if (event.kind === "offer") {
        desk.offer(event.actions);
      } else if (event.kind === "notice") {
        notices.receive(event.what, event.reason, event.endsInMs);
      } else {
        desk.alert(event.familyName, event.by, event.text);
      }
    },
    frozen: notices.frozen,
    held: (held) => notices.setHeld(held),
    closed: () => notices.closed(),
    ended: () => {
      notices.ended();
      desk.ended();
    },
  };
}
